/**
 * Admin 角色枚举 — 前端展示层统一使用的小写规范
 * =================================================================
 *
 * 后端管理库当前存储 `SUPER_ADMIN/OPERATOR/CS/RISK`。前端不直接透传这些值，
 * 统一归一化为 `super_admin/admin/customer_support` 做 UI 鉴权；创建/更新账号时
 * 再由页面映射回后端枚举。
 *
 * 三角色定义：
 *   - super_admin       : 超级管理员，唯一拥有高危操作权限
 *   - admin             : 普通管理员，可读所有业务模块，可写非高危
 *   - customer_support  : 客服，只读 + 用户冻结等运营操作
 *
 * 任何新增 / 重命名角色都必须同时落地：
 *   1. ADMIN_ROLES 元组
 *   2. divinheal-admin backend Role
 *   3. 数据库 admin_users.role 数据
 *   4. JWT claim 命名一致
 */

export const ADMIN_ROLES = ["admin", "super_admin", "customer_support"] as const;
export type AdminRole = (typeof ADMIN_ROLES)[number];

/** 高危操作放行的角色 */
export const HIGH_RISK_REQUIRED_ROLES: readonly AdminRole[] = ["super_admin"];

/**
 * 高危操作清单（与 settlement-service backend require_role 守卫一致）
 *
 * 任何前端按钮包裹 RequireRole 时，应在这里同步登记操作 key，
 * 便于全局 audit 哪些 UI 已强制 super_admin。
 */
export const HIGH_RISK_OPERATIONS = [
  "refund.approve",
  "config.update",
  "reconciliation.resolve",
  "address.delete",
] as const;

export type HighRiskOperation = (typeof HIGH_RISK_OPERATIONS)[number];

/** 运行时类型守卫 — 防御 localStorage / JWT 注入未知字符串 */
export function isAdminRole(value: unknown): value is AdminRole {
  return typeof value === "string" && (ADMIN_ROLES as readonly string[]).includes(value);
}

/** 检查 user role 是否在 required 列表内（兼容大小写历史值，先 normalize 再比对） */
export function hasRole(
  userRole: string | null | undefined,
  required: readonly AdminRole[]
): boolean {
  if (!userRole) return false;
  const normalized = normalizeAdminRole(userRole);
  if (!normalized) return false;
  return required.includes(normalized);
}

/**
 * 把任意历史 role 字符串归一化到当前 3 角色之一。
 *
 * 兼容映射来源：
 *   - 旧前端枚举 `SUPER_ADMIN / OPERATOR / CS / RISK`
 *   - 旧后端枚举 `owner / admin / operator / viewer`
 *   - 当前 DB 实际值 `super_admin / admin / customer_support`
 *
 * 未知值返回 null（调用方可 fallback 到 "admin" 或拒绝授权，由业务决定）。
 */
export function normalizeAdminRole(raw: string | null | undefined): AdminRole | null {
  if (!raw || typeof raw !== "string") return null;
  const lower = raw.trim().toLowerCase();

  switch (lower) {
    case "super_admin":
    case "superadmin":
    case "owner":
      return "super_admin";

    case "admin":
    case "operator":
    case "risk":
      return "admin";

    case "customer_support":
    case "customer-support":
    case "cs":
    case "viewer":
      return "customer_support";

    default:
      return null;
  }
}

/** 中文文案 — Topbar / RequireRole tooltip 等 UI 展示用 */
export const ADMIN_ROLE_LABELS: Record<AdminRole, string> = {
  super_admin: "超级管理员",
  admin: "管理员",
  customer_support: "客服",
};
